// components/SupabaseRpcProvider.tsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useMutablePlasmicQueryData } from '@plasmicapp/react-web/lib/query';
import { DataProvider } from '@plasmicapp/react-web/lib/host';
import { useSupabaseFromContext } from './SupabaseClientContext';
import { useRealtimeSubscription } from './RealtimeProvider';
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { isPlasmicProxy } from '../lib/isPlasmicProxy';
import { getProxyFingerprint } from '../lib/proxy-client';

const IS_CLIENT = typeof window !== 'undefined';
const isPrepass = () => !IS_CLIENT && !!(globalThis as any).__ssrPrepassEnv;

const stableJsonStringify = (obj: unknown): string => {
  if (obj === undefined || obj === null) return 'null';
  if (Array.isArray(obj)) return JSON.stringify([...obj].sort());
  if (typeof obj === 'object' && obj !== null)
    return JSON.stringify(obj, Object.keys(obj as object).sort());
  return JSON.stringify(obj);
};

interface SupabaseRpcProviderProps {
  children: React.ReactNode;
  rpcName: string;
  rpcParams?: Record<string, unknown>;
  realtimeTriggerTables?: string[];
  dataName?: string;
  queryKey?: string | readonly unknown[];
  alwaysClient?: boolean;
  onRealtimeTrigger?: (
    payload: RealtimePostgresChangesPayload<Record<string, unknown>>
  ) => void;
}

export function SupabaseRpcProvider({
  children,
  rpcName,
  rpcParams = {},
  realtimeTriggerTables = [],
  queryKey: customKeyFromProp,
  dataName,
  alwaysClient = false,
  onRealtimeTrigger,
}: SupabaseRpcProviderProps) {
  const supabase = useSupabaseFromContext();
  const [isMounted, setIsMounted] = useState(false);
  
  // Initialize proxy detection state
  const [useDevProxy] = useState(() => isPlasmicProxy());

  useEffect(() => { setIsMounted(true); }, []);

  const providerId = dataName || rpcName || "unknown_rpc";
  const inPrepass = isPrepass();

  const isKeyReady = useMemo(() => {
    if (!rpcName) return false;
    const paramsStr = JSON.stringify(rpcParams);
    return !paramsStr.includes('"undefined"') && !paramsStr.includes('"null"');
  }, [rpcName, rpcParams]);

  const queryKeyToUse = useMemo(() => {
    if ((inPrepass && alwaysClient) || !isKeyReady) return null;
    
    const baseKey = `/plasmicrpc/${rpcName}/${stableJsonStringify(rpcParams)}`;
    
    // During Hydration (Render 1), key matches server.
    // After mount in dev mode, we flip to -devproxy to handle Studio calls.
    return (useDevProxy && isMounted) ? `${baseKey}-devproxy` : baseKey;
  }, [rpcName, rpcParams, isKeyReady, useDevProxy, isMounted, inPrepass, alwaysClient]);

  const fetcher = useCallback(async () => {
    if (!queryKeyToUse || (inPrepass && alwaysClient)) return null;

    try {
      if (useDevProxy && IS_CLIENT) {
        // --- SECURE HANDSHAKE LOGIC ---
        const fingerprint = await getProxyFingerprint();

        const resp = await fetch('/api/plasmic-studio-proxy', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'x-proxy-fingerprint': fingerprint || ''
          },
          body: JSON.stringify({ operation: 'rpc', rpc: rpcName, params: rpcParams }),
        });
        
        if (!resp.ok) throw new Error(`Proxy RPC error ${resp.status}`);
        return await resp.json();
      } else {
        // Standard Direct Fetch (RLS applies)
        const { data: rpcData, error: rpcError } = await supabase.rpc(rpcName, rpcParams);
        if (rpcError) throw rpcError;
        return rpcData;
      }
    } catch (err: any) {
      console.error(`[SupabaseRpcProvider] Error in ${providerId}:`, err.message);
      throw err;
    }
  }, [rpcName, rpcParams, supabase, useDevProxy, inPrepass, alwaysClient, queryKeyToUse, providerId]);

  const { data } = useMutablePlasmicQueryData(customKeyFromProp || queryKeyToUse, fetcher, {
    revalidateOnMount: useDevProxy ? true : undefined,
    revalidateIfStale: useDevProxy,
  });

  if (inPrepass && data === undefined && queryKeyToUse && !alwaysClient) {
    throw fetcher(); 
  }

  useRealtimeSubscription(realtimeTriggerTables, onRealtimeTrigger);

  const is_loaded = data !== undefined;

  return (
    <DataProvider name={providerId} data={data ?? null}>
      <DataProvider name="is_loaded" data={is_loaded}>
        {children}
      </DataProvider>
    </DataProvider>
  );
}
